import { VIEWER_COMPONENTS_ID_PREFIXES } from './componentUtils'

// Self-contained open-check. getShouldUseClassSelector / getElementCompId (and this fn) are
// serialized via .toString() and eval'd in the browser (testkit getByCompIdForBrowser,
// e2e-drivers getElementCompIdForBrowser via Playwright page.evaluate), which has NO module
// scope — so the truthy set MUST live inside the function body. Referencing a module-scoped
// const or an import would throw ReferenceError in the browser. Mirrors the production source of
// truth @wix/thunderbolt-experiments#isExperimentOpen (which is never serialized and keeps its
// module-scoped const).
export function isExperimentOpen(experiments: any, spec: string): boolean {
	// FOR TESTING ONLY: add your experiment here to force it open while developing.
	// This list must stay empty on master — do NOT merge your additions.
	//
	// The twin of @wix/thunderbolt-experiments#FORCE_OPEN_EXPERIMENTS, which cannot be imported
	// here: this function is serialized, so the list has to live inside the body like the truthy set.
	// Only three specs are read in this file — useClassSelectorsForLookup, addIdAsClassName and
	// preserveWixSelectClass — and forcing any of them open needs an entry in BOTH lists. One alone
	// leaves the other side on the id path while the run still looks green.
	const FORCE_OPEN_DOM_EXPERIMENTS: Array<string> = []
	if (FORCE_OPEN_DOM_EXPERIMENTS.includes(spec)) {
		return true
	}
	return ['true', 'new', 'b', 'enabled'].includes(`${experiments?.[spec]}`.toLowerCase())
}

export interface DomSelectorOptions {
	experiments?: any
	document?: Document
	element?: Element
	logger?: any
	componentIds?: Set<string>
	isEditor?: boolean
	prefixes?: ReadonlyArray<string>
}

export function getShouldUseClassSelector(options: DomSelectorOptions = {}): boolean {
	let experiments = options?.experiments

	// Fallback to window.viewerModel.experiments if not provided
	if (!experiments && typeof window !== 'undefined') {
		try {
			const windowAny = window as any
			experiments = windowAny.viewerModel?.experiments
		} catch {
			// Silent fail - experiments not available yet
		}
	}

	if (!experiments) {
		return false
	}

	// specs.thunderbolt.plainClassSelectors deliberately does NOT open this gate: it only changes the
	// selectors Becky/Catharsis emit into the stylesheet, never how the runtime looks elements up.
	// Class-based lookup is owned by specs.thunderbolt.useClassSelectorsForLookup alone.
	const useClassSelectors = isExperimentOpen(experiments, 'specs.thunderbolt.useClassSelectorsForLookup')
	const addIdAsClassName = isExperimentOpen(experiments, 'specs.thunderbolt.addIdAsClassName')
	return !!(useClassSelectors && addIdAsClassName)
}

export function getDocument(options: DomSelectorOptions = {}): Document | null {
	return options.document || (typeof document !== 'undefined' ? document : null)
}

function logFallbackToId(logger: any, compId: string, selectorType: string): void {
	// Track fallback in BI to identify components missing className
	if (logger && typeof logger.meter === 'function') {
		logger.meter('dom_selector_id_fallback', {
			customParams: {
				compId,
				selectorType,
			},
		})
	}

	// Always log to console for debugging
	if (typeof console !== 'undefined' && console.warn) {
		console.warn(`[DOM Selectors] Fallback to ID for '${compId}' (${selectorType}).`)
	}
}

// jsdom does not implement CSS.escape. Referenced by getByCompIdForBrowser's serialized
// string in ./testkit, so it must stay self-contained (no imports, no module-scope refs) and
// must be a function declaration: an exported const arrow compiles to (0, exports.escapeIdent)(...)
// under tsc's CJS emit, which throws ReferenceError when the serialized string is eval'd in a browser.
export function escapeIdent(value: string): string {
	return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
		? CSS.escape(value)
		: value === '-'
			? '\\-'
			: value.replace(/[^a-zA-Z0-9_-]|\d/g, (ch, offset) => {
					if (/\d/.test(ch)) {
						return offset === 0 || (offset === 1 && value[0] === '-') ? `\\3${ch} ` : ch
					}
					return `\\${ch}`
				})
}

/**
 * Get element by component ID with optional class selector fallback.
 *
 * When experiments are enabled:
 * 1. Try querySelector with class selector (.compId)
 * 2. Fallback to getElementById if not found
 * 3. Log fallback for tracking
 *
 * When experiments are disabled:
 * - Use getElementById directly
 */
export function getByCompId(compId: string, options: DomSelectorOptions = {}): HTMLElement | null {
	const doc = getDocument(options)
	if (!doc) {
		return null
	}

	if (!compId || typeof compId !== 'string') {
		return null
	}

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const element = doc.querySelector(`.${escapeIdent(compId)}`) as HTMLElement | null
		if (element) {
			return element
		}
	}

	const element = doc.getElementById(compId)
	if (element && shouldUseClassSelector) {
		logFallbackToId(options?.logger, compId, 'getElementById')
	}
	return element
}

/**
 * Get component ID from an element.
 * Strategy: classList → structure lookup → prefix match → element.id
 *
 * @param element - The DOM element to get the component ID from
 * @param options - Options including experiments, componentIds Set, and isEditor flag
 * @returns The component ID or empty string if not found
 */
export function getElementCompId(element: Element, options: DomSelectorOptions = {}): string {
	// Guard: element must exist
	if (!element) {
		return ''
	}

	// Experiment off: use element.id
	if (!getShouldUseClassSelector(options)) {
		return element.id
	}

	const classes = Array.from(element.classList || [])

	// Editor optimization: skip non-component elements (no wix-select class)
	// Only enabled when preserveWixSelectClass experiment is on (ensures wix-select class is actually present)
	const preserveWixSelectClass = isExperimentOpen(options.experiments, 'specs.thunderbolt.preserveWixSelectClass')
	// wix-select is emitted only by the DS bundle's StructureComponent, but DS callers query
	// viewer-rendered documents too (preview-region / Blocks iframes via componentWindowResolver).
	// The skip is valid only in this bundle's own document; a foreign document has no wix-select
	// on any element, so every element would bail out here.
	const isForeignDocument = typeof document !== 'undefined' && element.ownerDocument !== document
	if (options.isEditor && preserveWixSelectClass && !isForeignDocument && !classes.includes('wix-select')) {
		return ''
	}

	// Strategy 1: Structure lookup (O(1) Set lookup)
	// Check against known component IDs from the page structure
	if (options.componentIds?.size) {
		// Single pass:
		// - Prefer repeater item matches (with __) - return immediately
		// - else exact matches (structure contains the full ID)

		const repeaterItemMatches = classes.filter((cls) => cls.includes('__'))
		for (const cls of repeaterItemMatches) {
			const delimiterIndex = cls.indexOf('__')
			const baseId = cls.substring(0, delimiterIndex)
			if (options.componentIds.has(baseId)) {
				return cls
			}
		}

		for (const cls of classes) {
			if (options.componentIds.has(cls)) {
				return cls
			}
		}
	}

	// Strategy 2: Prefix matching (comp-*, TPASection_*, etc.)
	// Used when componentIds Set is not available.
	// options.prefixes allows callers in browser eval contexts to inject the prefixes
	// as a plain serializable value, avoiding the compiled exports.X reference.
	const prefixes = options.prefixes ?? VIEWER_COMPONENTS_ID_PREFIXES
	let shortestMatch: string | null = null

	for (const cls of classes) {
		const hasPrefix = prefixes.some((prefix) => cls.startsWith(prefix))

		if (hasPrefix) {
			// Prefer repeater items (with __) - return immediately
			if (cls.includes('__')) {
				return cls
			}
			// Track shortest non-repeater to avoid -container suffixes
			if (!shortestMatch || cls.length < shortestMatch.length) {
				shortestMatch = cls
			}
		}
	}

	if (shortestMatch) {
		return shortestMatch
	}

	// Fallback to element.id (for timing issues during SSR hydration)
	if (element.id) {
		logFallbackToId(options.logger, element.id, 'getElementCompId')
	}
	return element.id || ''
}

/**
 * Converts an ID-based selector to a class-based selector.
 * Handles various selector patterns:
 * - #id → .id
 * - [id="value"] → [class~="value"]
 * - [id^="value"] → :is([class^="value"],[class*=" value"]) — matches prefix as first or any subsequent class token
 * - [id*="value"] → [class*="value"]
 * - [id$="value"] → [class$="value"]
 *
 * @param selector - The ID-based selector to convert
 * @returns The class-based selector
 */
export function convertIdSelectorToClassSelector(selector: string): string {
	return (
		selector
			// Replace #id with .id
			.replace(/#([a-zA-Z0-9_-]+)/g, '.$1')
			// Replace [id="value"] with [class~="value"] (exact match in space-separated list)
			.replace(/\[id="([^"]+)"\]/g, '[class~="$1"]')
			// Replace [id^="value"] with :is([class^="value"],[class*=" value"])
			// [class^="value"] matches when the class is first; [class*=" value"] matches any subsequent token.
			// Together they reliably match a prefix regardless of class order in the attribute.
			.replace(/\[id\^="([^"]+)"\]/g, ':is([class^="$1"],[class*=" $1"])')
			// Replace [id*="value"] with [class*="value"] (contains)
			.replace(/\[id\*="([^"]+)"\]/g, '[class*="$1"]')
			// Replace [id$="value"] with [class$="value"] (ends with)
			.replace(/\[id\$="([^"]+)"\]/g, '[class$="$1"]')
	)
}

/**
 * Creates experiment-aware CSS selector for Becky/Catharsis.
 * Returns ID-only or dual-mode :is(class, id) selector based on experiment flag.
 * @param addWixSelectClass - When true, appends .wix-select to the class part only,
 * scoping matches to root components (requires preserveWixSelectClass experiment).
 */
export function toDualModeCssSelector(
	idSelector: string,
	addIdAsClassName: boolean,
	addWixSelectClass = false
): string {
	if (!addIdAsClassName) {
		return idSelector
	}
	const classSelector = convertIdSelectorToClassSelector(idSelector)
	return `:is(${classSelector}${addWixSelectClass ? '.wix-select' : ''}, ${idSelector})`
}

/**
 * Generic querySelector that automatically converts ID-based selectors to class-based selectors
 * when the experiment is enabled. Falls back to ID-based selectors if no elements are found.
 *
 * @param selector - The selector string (can include #id, [id^="..."], etc.)
 * @param options - DOM selector options
 * @returns The first matching element or null
 */
export function querySelector(selector: string, options: DomSelectorOptions = {}): Element | null {
	// Use element if provided, otherwise fall back to document
	const container = options.element || getDocument(options)

	if (!container) {
		return null
	}
	if (!selector || typeof selector !== 'string') {
		return null
	}

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const classSelector = convertIdSelectorToClassSelector(selector)
		const element = container.querySelector(classSelector)
		if (element) {
			return element
		}
	}

	const element = container.querySelector(selector)
	if (element && shouldUseClassSelector) {
		logFallbackToId(options.logger, selector, 'querySelector')
	}

	return element
}

/**
 * Generic querySelectorAll that automatically converts ID-based selectors to class-based selectors
 * when the experiment is enabled. Falls back to ID-based selectors if no elements are found.
 *
 * @param selector - The selector string (can include #id, [id^="..."], etc.)
 * @param options - DOM selector options
 * @returns Array of matching elements
 */
export function querySelectorAll(selector: string, options: DomSelectorOptions = {}): Element[] {
	// Use element if provided, otherwise fall back to document
	const container = options.element || getDocument(options)

	if (!container) {
		return []
	}
	if (!selector || typeof selector !== 'string') {
		return []
	}

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const classSelector = convertIdSelectorToClassSelector(selector)
		const elements = Array.from(container.querySelectorAll(classSelector))
		if (elements.length > 0) {
			return elements
		}
	}

	const elements = Array.from(container.querySelectorAll(selector))
	if (elements.length > 0 && shouldUseClassSelector) {
		logFallbackToId(options.logger, selector, 'querySelectorAll')
	}

	return elements
}

/**
 * Find closest ancestor element by component ID.
 * Supports both class-based and ID-based selectors.
 *
 * @param element - The starting element
 * @param compId - The component ID to search for
 * @param options - DOM selector options
 * @returns The closest matching ancestor element or null
 */
export function getClosestByCompId(element: Element, compId: string, options: DomSelectorOptions = {}): Element | null {
	if (!compId || typeof compId !== 'string') {
		return null
	}

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const closestElement = element.closest(`.${escapeIdent(compId)}`)
		if (closestElement) {
			return closestElement
		}
	}

	const closestElement = element.closest(`#${escapeIdent(compId)}`)
	if (closestElement && shouldUseClassSelector) {
		logFallbackToId(options.logger, compId, 'getClosestByCompId')
	}

	return closestElement
}

/**
 * Find closest ancestor element matching a selector.
 * Automatically converts ID-based selectors to class-based selectors when experiment is enabled.
 * Falls back to ID-based selectors if no elements are found.
 *
 * @param element - The starting element
 * @param selector - The selector string (can include #id, [id^="..."], etc.)
 * @param options - DOM selector options
 * @returns The closest matching ancestor element or null
 */
export function closest(element: Element, selector: string, options: DomSelectorOptions = {}): Element | null {
	if (!selector || typeof selector !== 'string') {
		return null
	}

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const classSelector = convertIdSelectorToClassSelector(selector)
		const closestElement = element.closest(classSelector)
		if (closestElement) {
			return closestElement
		}
	}

	const closestElement = element.closest(selector)
	if (closestElement && shouldUseClassSelector) {
		logFallbackToId(options.logger, selector, 'closest')
	}

	return closestElement
}
